import Swiper from '../../plugins/swiper';
import { BaseElements, BaseObject, BaseService, BaseInjector } from '../../js/base';
import { querySelector, KeyCodes, isIE } from '../../utility/utils';
import bootstrapBreakpoints from '../../utility/grid';
import { offVPort, onVPort, ScreenSize } from '../../utility/breakPointEvents';

const events = {
    init: 'UX:swiper-init',
    destroy: 'UX:swiper-destroy'
};

class Texts extends BaseObject { }

class ClassList extends BaseObject {
    paused = 'paused';
}

class Selectors extends BaseObject {
    container = '.swiper-container';
    pagination = '.swiper-pagination';
    prev = '.swiper-button-prev';
    next = '.swiper-button-next';
    responsiveSlider = '.responsive-slider';
    viewportSlider = '.viewport-slider';
    centerSlider = '.center-slider';
    freeModeSlider = '.freemode-slider';
    marginSlider = '.margin-slider';
    observerSlider = '.observer-slider';
}

class Elements extends BaseElements<Selectors> {
    swiper: HTMLElement;
    pagination: HTMLElement;
    next: HTMLElement;
    prev: HTMLElement;
    wrapper: HTMLElement

    constructor(selectors: Selectors) {
        super(selectors, false);
    }

    setWrapper(wrapper: HTMLElement) {
        this.wrapper = wrapper;
        this.initElements();
    }

    initElements() {
        this.swiper = querySelector(this.selectors.container, this.wrapper);
        this.pagination = querySelector(this.selectors.pagination, this.wrapper);
        this.next = querySelector(this.selectors.next, this.wrapper);
        this.prev = querySelector(this.selectors.prev, this.wrapper);
    }
}

interface SlideShow extends BaseService<Selectors, Elements, ClassList, Texts> { }

interface SlideShowOptions {
    autoHeight: (string | boolean);
    loop: boolean;
    effect: string;
    loopAdditionalSlides: (number | null);
    centeredSlides: boolean;
    slidesPerView: (number | string);
    spaceBetween: (string | number);
    breakpoints: object;
    freeMode: boolean;
    a11y: {
        enabled: boolean,
        prevSlideMessage: string,
        nextSlideMessage: string,
        paginationBulletMessage: string
    },
    pagination: { el: HTMLElement, clickable: boolean };
    navigation: { nextEl: string | HTMLElement, prevEl: string | HTMLElement };
    init: boolean;
    clickable: boolean;
    grabCursor: boolean;
    observeParents: boolean;
    observer: HTMLElement;
    autoplay: {
        delay: number,
        disableOnInteraction: boolean,
    } | boolean;
    on: {
        slideChangeTransitionEnd: (swiperElm: { slides: any }) => any

    }
}

@BaseInjector(Selectors, Elements, ClassList, Texts)
class SlideShow {

    initEvent: Event;
    destoryEvent: Event;

    constructor(readonly wrapper: HTMLElement) {

    }


    initialize(): void {
        this.elements.setWrapper(this.wrapper);
        this.initEvent = this.getCustomEvent(events.init);
        this.destoryEvent = this.getCustomEvent(events.destroy);

        this.beforeInstanceCreation(this.elements.swiper);
        this.initializeInstance();
        this.afterInstanceCreation(this.elements.swiper);
    }

    getCustomEvent(type: string): Event {
        return new Event(type);
    }

    /**
     * Initialize the swiper instance and apply accessibility on the swiper element
     *
     */
    initializeInstance(addViewPortListener: boolean = true): Swiper {
        const swiperEl = this.elements.swiper;
        const options = this.options();
        let swiper;
        const readySwiper = (Swiper) => {
            swiper = new Swiper(swiperEl, options);
            this.addEventListeners(swiper);
            if (addViewPortListener) {
                const viewPort = this.setViewPortListener(options);
                if (!viewPort || (viewPort && window.outerWidth < bootstrapBreakpoints[viewPort])) {
                    swiper.init();
                    swiperEl.dispatchEvent(this.initEvent);
                }
            }
        };

        if (isIE()) {
            //Latest supported version of IE for swiper is v4, therefore it will be added dynamically
            import('swiper-v4')
                .then(({ default: Swiper }) => {
                    readySwiper(Swiper);
                    return swiper;
                })
                .catch(e => console.log(e));
        } else {
            readySwiper(Swiper);
            return swiper;
        }
    }

    /**
     * Add extra logic before the instance initialization
     *
     */
    beforeInstanceCreation(swiperEl: HTMLElement) {

    }

    /**
     * Add extra logic after the instance initialization
     *
     */
    afterInstanceCreation(swiperEl: HTMLElement) {
        this.addAccessibility(swiperEl);
        this.addAutoPlayControl();
    }

    /**
     * Generate the options for the swiper from the DOM.
     *
     */
    options(): SlideShowOptions {
        const swiper = this.elements.swiper;
        const options = swiper.dataset ?? {};
        const speed = parseInt(options.speed);
        const items = parseInt(options.items);
        const autoplay = options.autoplay && options.autoplay !== 'false' ? {
            delay: speed,
            disableOnInteraction: false,
        } : false;
        const loopAdditionalSlides = options.loop ? 2 : null; //clone additional two slides to prevent the issue of extra next/prev items not showing
        return {
            a11y: {
                enabled: !options.disableA11y,
                prevSlideMessage: options.prevSlide,
                nextSlideMessage: options.nextSlide,
                paginationBulletMessage: `${options.goToSlide} {{index}}`
            },
            spaceBetween: this.swiperMargin,
            breakpoints: this.responsiveOption,
            centeredSlides: this.centeredOption,
            freeMode: this.freeModeOption,
            pagination: this.pagination,
            navigation: this.navigation,
            init: false,
            autoHeight: (options.autoheight && options.autoheight !== 'false'),
            effect: options.animation,
            slidesPerView: !isNaN(items) ? items : 'auto',
            loop: (options.loop && options.loop != 'false'),
            loopAdditionalSlides: loopAdditionalSlides,
            clickable: true,
            grabCursor: true,
            observeParents: true,
            observer: this.hasObserver,
            autoplay,
            on: {
                slideChangeTransitionEnd: this.slideChangeTransitionEnd.bind(this)
            }
        };
    }

    /**
     * Generates the indicators option for the swiper
     *
     */
    get pagination(): SlideShowOptions["pagination"] {
        return {
            el: this.elements.pagination,
            clickable: true
        };
    }

    /**
     * Generates the arrows option for the swiper
     *
     */
    get navigation(): SlideShowOptions["navigation"] {
        return {
            nextEl: this.elements.next,
            prevEl: this.elements.prev
        };
    }

    /**
     * Check if the parent of the swiper-container has a class of margin-slider
     *
     */
    get swiperMargin(): SlideShowOptions["spaceBetween"] {
        const marginSlider = this.elements.swiper.closest<HTMLElement>(this.selectors.marginSlider);
        let spaceBetween = 0;
        if (marginSlider && marginSlider.dataset.margin) {
            spaceBetween = parseInt(marginSlider.dataset.margin);
        }
        return spaceBetween;
    }

    /**
     * Check if the parent need to enable the observer option
     *
     */
    get hasObserver(): HTMLElement {
        return this.elements.swiper.closest<HTMLElement>(this.selectors.observerSlider);
    }

    /**
     * Check if the parent of the swiper-container has a class of center-slider
     *
     */
    get centeredOption(): SlideShowOptions["centeredSlides"] {
        return !!this.elements.swiper.closest<HTMLElement>(this.selectors.centerSlider);
    }

    /**
     * Check if the parent of the swiper-container has a class of freemode-slider
     *
     */
    get freeModeOption(): SlideShowOptions["freeMode"] {
        return !!this.elements.swiper.closest<HTMLElement>(this.selectors.freeModeSlider);
    }

    /**
     * Check if the parent of the swiper-container has timeout
     *
     */
    get initTimeout(): string {
        return this.elements.swiper.dataset.timeout;
    }

    /**
     * Generates the breakpoints option for the swiper
     *
     */
    get responsiveOption(): SlideShowOptions["breakpoints"] {
        const swiper = this.elements.swiper;
        let responsive = swiper.dataset?.responsive;
        if (!responsive || responsive === '') {
            responsive = swiper.closest<HTMLElement>(this.selectors.responsiveSlider)?.dataset?.responsive;
        }

        let breakpoints = {};


        if (responsive && responsive !== '') {
            const json = JSON.parse(responsive);
            for (let key in json) {
                let viewport = bootstrapBreakpoints[key];
                breakpoints[viewport] = json[key];
            }
        }
        return breakpoints;
    };

    /**
     * Initialize the instance or destroy it depending on the given viewport
     *
     */
    setViewPortListener(options: object): string {
        const viewPort = this.elements.swiper.closest<HTMLElement>(this.selectors.viewportSlider)?.dataset?.sliderVport as ScreenSize;
        if (!viewPort) {
            return;
        }
        onVPort(viewPort, this.onViewPort.bind(this, options));
        offVPort(viewPort, this.offViewPort.bind(this, options));
        return viewPort.split('-').pop();
    };

    /**
     * When enters the viewport
     *
     * @param options
     */
    onViewPort(options) {
        const swiperEl = this.elements.swiper;
        if (!swiperEl.swiper || !swiperEl.swiper.initialized) {
            this.beforeInstanceCreation(swiperEl);
            this.initializeInstance(false).init();
            this.afterInstanceCreation(swiperEl);
        } else {
            swiperEl.swiper.init();
        }
        swiperEl.dispatchEvent(this.initEvent);
    }

    /**
     * When leaves the viewport
     *
     */
    offViewPort() {
        const swiperEl = this.elements.swiper;
        if (swiperEl.swiper && swiperEl.swiper.initialized) {
            swiperEl.swiper.destroy();
        }
        swiperEl.dispatchEvent(this.destoryEvent);
    }

    /**
     * Add event listeners for the current instance
     *
     */
    addEventListeners(swiper: Swiper) {

    }

    /**
     * On transition end
     *
     */
    slideChangeTransitionEnd(swiper: Swiper) { }

    /**
     * Check if the parent of the swiper-container has timeout
     *
     */
    addAccessibility(swiperEl: HTMLElement) {
        const swiper = swiperEl.swiper;
        const isAutoplayEnabled = swiperEl.dataset?.autoplay === 'true';
        if (swiper) {
            swiper.slides.forEach((slide, i) => {
                slide.addEventListener('focusin', () => {
                    // Reset the scroll
                    swiperEl.scrollLeft = 0;
                    // WebKit sets the scroll after the event, we need
                    // to reset it with zero timeout. Keep the first
                    // reset to prevent jittering in other browsers
                    setTimeout(() => {
                        swiperEl.scrollLeft = 0;
                    }, 0);
                    swiper.slideTo(i);
                });
            });
            swiperEl.addEventListener('focusin', () => {
                this.keyboardFocusControls(swiperEl, swiper, isAutoplayEnabled, true);
            });
            swiperEl.addEventListener('focusout', () => {
                this.keyboardFocusControls(swiperEl, swiper, isAutoplayEnabled, false);
            });
        }
    }

    /**
     * Control the keyboard functionalities when the swiper has focus
     *
     */
    keyboardFocusControls(swiperEl: HTMLElement, swiper: Swiper, isAutoplayEnabled: boolean, enable: boolean) {
        if (!swiper || !swiper.keyboard) {
            return;
        }
        if (enable) {
            swiper.keyboard.enable();
            swiperEl.addEventListener('keydown', this.handleKeydown.bind(this, swiper));
            isAutoplayEnabled ? swiper.autoplay.stop() : '';
        } else {
            swiper.keyboard.disable();
            swiperEl.removeEventListener('keydown', this.handleKeydown.bind(this, swiper));
            isAutoplayEnabled ? swiper.autoplay.start() : '';
        }
    }

    /**
     * Handle keydown event on the swiper
     *
     */
    handleKeydown(swiper: Swiper, e: KeyboardEvent) {
        if (e.keyCode === KeyCodes.LEFT || e.keyCode === KeyCodes.RIGHT) {
            // Add timeout to allow the active index to be changed first
            setTimeout(() => {
                const activeSlide = swiper.slides[swiper.activeIndex];
                $(activeSlide).find(':focusable').first().eq(0).focus();
            }, 0);
        }
    }

    protected addAutoPlayControl() {
        const { pagination, swiper } = this.elements;        
        const { controls, autoplay } = swiper.dataset;
        if(!pagination || !swiper || controls !=="true" || autoplay!=="true") return;  
        const controlBtn = this.base.stringToNode(`<span class="swiper-pagination-bullet swiper-control-btn mx-16 d-flex justify-content-center align-items-center order-first" tabindex="0" role="button" aria-label="Pause"><i class="icon-pause"></i><i class="icon-play"></i></span>`);
        controlBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            if(controlBtn.classList.contains(this.classList.paused)) {
                (swiper as any).swiper.autoplay.start();
                controlBtn.classList.remove(this.classList.paused);
            } else {
                (swiper as any).swiper.autoplay.stop();
                controlBtn.classList.add(this.classList.paused);
            }
        })
        pagination.append(controlBtn);
    }
}

export default SlideShow;

export {
    Elements as SlideShowElements,
    Selectors as SlideShowSelectors,
    ClassList as SlideShowClassList,
    Texts as SlideShowTexts,
    SlideShowOptions,
    events,
}